import React, { useState, useEffect, useRef } from 'react';
import HireCard from './HireCard';
import { useScrollOpenHireIntoView } from './hireHighlight';
import { bucketHires } from '../../utils/hireBuckets';

// Default mobile collapse state — actionable columns expanded, history collapsed
const DEFAULT_OPEN = { upcoming: true, on_hire: true, ending_soon: true, off_hired: false };

/**
 * Four-column Kanban board for hire items.
 * Columns: Upcoming (pending), On Hire (on_hire with hire_to > 7 days), Ending Soon (on_hire/overdue with hire_to <= 7 days), Off-Hired (off_hired)
 * — the 7 is `ENDING_SOON_WINDOW_DAYS` in `utils/hireBuckets.js`.
 */
function KanbanView({ items, canManage, onExtend, onOffHire, onReHire, onViewOrder, onCancelOrder, onOpenHire, openItemId = null }) {
  // The hire the Table has open is marked and scrolled into view
  // (`hireHighlight.js`; owner 2026-09-28).
  const boardRef = useRef(null);
  useScrollOpenHireIntoView(openItemId, boardRef);
  const [openCols, setOpenCols] = useState(() => {
    try {
      const raw = localStorage.getItem('sfx_hires_kanban_sections');
      return raw ? { ...DEFAULT_OPEN, ...JSON.parse(raw) } : DEFAULT_OPEN;
    } catch {
      return DEFAULT_OPEN;
    }
  });

  useEffect(() => {
    try { localStorage.setItem('sfx_hires_kanban_sections', JSON.stringify(openCols)); } catch { /* ignore */ }
  }, [openCols]);

  const toggleCol = (id) => setOpenCols(p => ({ ...p, [id]: !p[id] }));

  // Sort items into columns — THE shared classification (`utils/hireBuckets.js`,
  // extracted from this file 2026-09-27 so the hires Table view reads the same
  // answer). Membership and order unchanged: `KanbanView.columns.test.js`.
  const { upcoming, onHire, endingSoon, offHired } = bucketHires(items);

  const columns = [
    {
      id: 'upcoming',
      title: 'Upcoming',
      count: upcoming.length,
      items: upcoming,
      headerBg: 'bg-amber-50',
      headerBorder: 'border-amber-200',
      headerText: 'text-amber-800',
      headerTextDark: 'dark:text-amber-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: 'on_hire',
      title: 'On Hire',
      count: onHire.length,
      items: onHire,
      headerBg: 'bg-blue-50',
      headerBorder: 'border-blue-200',
      headerText: 'text-blue-800',
      headerTextDark: 'dark:text-green-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      id: 'ending_soon',
      title: 'Ending Soon',
      count: endingSoon.length,
      items: endingSoon,
      headerBg: 'bg-orange-50',
      headerBorder: 'border-orange-200',
      headerText: 'text-orange-800',
      headerTextDark: 'dark:text-orange-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4.832c-.77-1.333-2.694-1.333-3.464 0L3.34 16.168c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    {
      id: 'off_hired',
      title: 'Off-Hired',
      count: offHired.length,
      items: offHired,
      headerBg: 'bg-gray-50',
      headerBorder: 'border-gray-200',
      headerText: 'text-gray-700',
      headerTextDark: 'dark:text-red-400',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ),
    },
  ];

  return (
    <div ref={boardRef} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="kanban-view">
      {columns.map(col => {
        const isOpen = openCols[col.id];
        return (
          <div key={col.id} className="flex flex-col" data-testid={`kanban-column-${col.id}`}>
            {/* Column header — tappable toggle on mobile, static on desktop */}
            <button
              type="button"
              onClick={() => toggleCol(col.id)}
              data-section-title
              aria-expanded={isOpen}
              className={`${col.headerBg} ${col.headerBorder} dark:border-gray-600 border rounded-t-lg ${!isOpen ? 'rounded-b-lg md:rounded-b-none' : ''} px-4 py-3 min-h-[44px] md:min-h-0 flex items-center gap-2 w-full text-left md:cursor-default`}
            >
              {/* Mobile-only chevron */}
              <svg
                className={`md:hidden h-4 w-4 ${col.headerText} ${col.headerTextDark} transition-transform ${isOpen ? '' : '-rotate-90'}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              <span className={`${col.headerText} ${col.headerTextDark}`}>{col.icon}</span>
              <h3 className={`text-sm font-semibold ${col.headerText} ${col.headerTextDark}`}>{col.title}</h3>
              <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${col.headerBg} dark:bg-gray-700 ${col.headerText} ${col.headerTextDark} border ${col.headerBorder} dark:border-gray-600`}>
                {col.count}
              </span>
            </button>

            {/* Column body — always shown on desktop; mobile respects openCols */}
            <div className={`${isOpen ? 'block' : 'hidden'} md:block flex-1 bg-gray-50 dark:bg-gray-900 border border-t-0 border-gray-200 dark:border-gray-600 rounded-b-lg p-3 md:min-h-[200px]`}>
              {col.items.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-gray-400 dark:text-gray-500 italic py-8">
                  No {col.title.toLowerCase()} items
                </div>
              ) : (
                col.items.map(item => (
                  <HireCard
                    key={item.item_id}
                    item={item}
                    canManage={canManage}
                    onExtend={onExtend}
                    onOffHire={onOffHire}
                    onReHire={onReHire}
                    onViewOrder={onViewOrder}
                    onCancelOrder={onCancelOrder}
                    onOpenHire={onOpenHire}
                    openItemId={openItemId}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default KanbanView;
